// 移植自 crm/src/components/OverlayScroll.jsx。浮動式捲軸：隱藏原生捲軸（不佔空間），
// 改用 absolute 的 thumb 浮在內容右側；平時透明、hover／拖曳時顯示，可拖曳捲動。
// props（舊版 class／xxxClass 改名為 className／xxxClassName，用法相同）：
//   className         — 套在「內容層」（padding / flex 等版面寫這裡）
//   rootClassName     — 外層（預設 h-full；放在 flex 容器內時傳 'flex-1'）
//   viewportClassName — 捲動層（預設 min-h-0 flex-1）。不能用 h-full：祖先高度若是「auto 被 max-height 夾住」
//                       （如 Dialog 卡片），百分比高會退化成 auto → 永遠不捲。無定高父層時改傳 'max-h-XX'，root 傳 ''
//   viewportStyle     — 捲動層 inline style（最大高度是動態 px 時用）
//   viewportRef       — 取得捲動層 DOM（ref 物件或 callback 都可）
//   thumbClassName    — 覆寫 thumb 位置／寬度（預設 'right-1 w-1.5'；下拉面板依 Figma 10240:3222 見舊版說明）
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

export function OverlayScroll({ className, rootClassName, viewportClassName, viewportStyle, viewportRef, thumbClassName, children }) {
  const rootRef = useRef(null)
  const vpRef = useRef(null)
  const contentRef = useRef(null)
  const draggingRef = useRef(false)
  const [m, setM] = useState({ h: 0, top: 0 })
  const [active, setActive] = useState(false)

  const update = useCallback(() => {
    const vp = vpRef.current
    if (!vp) return
    const { scrollTop, scrollHeight, clientHeight } = vp
    if (scrollHeight - clientHeight <= 1) return setM({ h: 0, top: 0 })
    const h = Math.max(24, (clientHeight / scrollHeight) * clientHeight)
    setM({ h, top: (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - h) })
  }, [])

  useEffect(() => {
    update()
    const ro = new ResizeObserver(update)
    ro.observe(vpRef.current)
    if (contentRef.current) ro.observe(contentRef.current)
    return () => ro.disconnect()
  }, [update])

  // hover 用原生 mouseenter/mouseleave（依 DOM 樹，同舊版 Solid）：React 的合成事件依元件樹，
  // Portal 到 body 的下拉面板會被算成「在裡面」，滑鼠停在面板上捲軸就亮
  useEffect(() => {
    const el = rootRef.current
    const enter = () => setActive(true)
    const leave = () => {
      if (!draggingRef.current) setActive(false)
    }
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mouseleave', leave)
    return () => {
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
    }
  }, [])

  // 把捲動層 DOM 交給呼叫端（ref 物件或 callback 都可）
  useImperativeHandle(viewportRef, () => vpRef.current, [])

  const onThumbDown = (e) => {
    e.preventDefault()
    const vp = vpRef.current
    draggingRef.current = true
    setActive(true)
    const y0 = e.clientY
    const s0 = vp.scrollTop
    const range = vp.scrollHeight - vp.clientHeight
    const track = vp.clientHeight - m.h
    const move = (ev) => {
      if (draggingRef.current && track > 0) vp.scrollTop = s0 + ((ev.clientY - y0) / track) * range
    }
    const up = () => {
      draggingRef.current = false
      setActive(false)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  return (
    <div ref={rootRef} className={`relative flex min-h-0 flex-col ${rootClassName ?? 'h-full'}`}>
      <div ref={vpRef} onScroll={update} style={viewportStyle} className={`scrollbar-none overflow-y-auto ${viewportClassName ?? 'min-h-0 flex-1'}`}>
        <div ref={contentRef} className={className}>
          {children}
        </div>
      </div>
      {m.h > 0 && (
        // thumb 只是滑鼠拖曳的輔助；鍵盤用方向鍵／PageDown 捲動內容即可，不需要能聚焦
        <div
          onPointerDown={onThumbDown}
          className={`absolute z-10 cursor-pointer rounded-full bg-brand-black-1000-a25 transition-opacity duration-200 ${thumbClassName ?? 'right-1 w-1.5'} ${active ? 'opacity-100' : 'opacity-0'}`}
          style={{ height: `${m.h}px`, top: `${m.top}px` }}
        />
      )}
    </div>
  )
}
